import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, CheckCircle2, FileText, Loader2, Mic, Play, Printer, Save, Sparkles, X } from 'lucide-react';
import AssociativeAppShell from '../../components/AssociativeAppShell';
import RichEditor from '../../components/RichEditor';
import VerbaleTestata from '../../components/verbali/VerbaleTestata';
import VerbaleTappe from '../../components/verbali/VerbaleTappe';
import VerbaleAvvio, { FinestraImpagina } from '../../components/verbali/VerbaleAvvio';
import VerbaleAssistente from '../../components/verbali/VerbaleAssistente';
import VerbaleSezioni from '../../components/verbali/VerbaleSezioni';
import VerbaleDettagli, { FirmeVerbale, PartecipanteEsterno } from '../../components/verbali/VerbaleDettagli';
import VerbaleAllegati, { AllegatoVerbale } from '../../components/verbali/VerbaleAllegati';
import VerbalePubblica from '../../components/verbali/VerbalePubblica';
import VerbaleVersioni from '../../components/verbali/VerbaleVersioni';
import ConfirmDialog from '../../components/ConfirmDialog';
import VerbaleConsenso from '../../components/verbali/VerbaleConsenso';
import VerbaleRegistrazione, { registrazioneInCorso } from '../../components/verbali/VerbaleRegistrazione';
import VerbaleCaricaAudio, { VerbaleCaricaAudioHandle } from '../../components/verbali/VerbaleCaricaAudio';
import VerbaleAudioCard from '../../components/verbali/VerbaleAudioCard';
import VerbaleTrascrizione, { audioConTrascrizione } from '../../components/verbali/VerbaleTrascrizione';
import VerbaleVoci from '../../components/verbali/VerbaleVoci';
import VerbaleGenera, { FineGenerazione } from '../../components/verbali/VerbaleGenera';
import VerbaleFoto from '../../components/verbali/VerbaleFoto';
import VerbaleSocial from '../../components/verbali/VerbaleSocial';
import VerbaleLettura from '../../components/verbali/VerbaleLettura';
import { ISTRUZIONE_RIPRENDI, TESTO_GENERAZIONE_IN_CORSO_PAGINA } from '../../components/verbali/testiAudio';
import { RegistrazioneVerbale, useRegistrazioneVerbale } from '../../hooks/useRegistrazioneVerbale';
import { useToast } from '../../contexts/ToastContext';
import {
  getRepEvent, getRepEventVerbale, removeRepresentativeToken, saveRepEventVerbale,
} from '../../services/representativeService';
import {
  elencaFotoVerbale, FotoVerbale, leggiSocialVerbale, riscriviVerbale, salvaDettagliVerbale, scaricaPdfVerbale, StatoSocial,
} from '../../services/verbaliService';
import {
  DatiAudioVerbale, elencaAudio, eliminaAudio, erroreRichiesta, leggiVoci, riprendiDallaTrascrizione, trascriviAudio,
} from '../../services/verbaliAudioService';
import { aggiornaPartecipantiEsterni, dividiInSezioni, scheletroVerbale } from '../../utils/verbaleSezioni';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { paragrafoAlCursore, RiferimentoVerbale, riferimentiDa, trovaRiferimento } from '../../utils/verbaliRiferimenti';
import { formattaMs } from '../../utils/verbaliTrascrizione';
import { evidenziaSegni, selezionaSegno, sezioneDelSegno, togliEvidenziazione, trovaSegniDaVerificare } from '../../utils/verbaleDaVerificare';
import type { VerbaleLetturaDati } from '../../services/verbaliLettoriService';
import { nomeFilePdfVerbale } from '../../utils/scaricaPdf';
import '../../styles/verbale-foglio.css';

/**
 * Pagina del verbale a tutta larghezza (layout C v2, spec §4.7): foglio al 70%
 * a sinistra, assistente, dettagli, allegati e pubblicazione al 30% a destra;
 * sotto `lg` tre schede. L'autore scrive, gli altri leggono (pubblicato) o
 * vedono che il verbale non è ancora disponibile (bozza).
 */

const RITARDO_SALVATAGGIO = 3000;
/** Ogni quanto si ricontrolla un'impaginazione in corso sul server. */
const INTERVALLO_CONTROLLO_IMPAGINAZIONE = 10000;

const AZIONI_RAPIDE_FOGLIO = [
  { etichetta: 'Riscrivi', istruzione: 'Riscrivi in modo più chiaro e formale, stesso contenuto' },
  { etichetta: 'Accorcia', istruzione: 'Accorcia il testo mantenendo fatti, decisioni, nomi e numeri' },
  { etichetta: 'Più formale', istruzione: 'Rendi il testo più formale, adatto a un verbale ufficiale, stesso contenuto' },
  { etichetta: 'Correggi', istruzione: 'Correggi ortografia e grammatica senza cambiare il contenuto' },
];

// -- Audio (piano 2): accesi solo per l'autore con `puoRegistrare` (permesso associativa.verbali.registra) --

/** Ogni quanto si rilegge l'elenco degli audio mentre uno è in coda o in trascrizione. */
const INTERVALLO_CONTROLLO_AUDIO = 10000;
/** Azione rapida dell'editor che rilegge la trascrizione (solo con un audio trascritto). */
const AZIONE_RIPRENDI = { etichetta: 'Riprendi dalla trascrizione', istruzione: ISTRUZIONE_RIPRENDI };
const AZIONI_RAPIDE_CON_TRASCRIZIONE = [...AZIONI_RAPIDE_FOGLIO, AZIONE_RIPRENDI];
export const TESTO_USCITA_REGISTRAZIONE = 'La registrazione continua solo se resti in questa pagina';
export const TESTO_TRASCRIZIONE_PRONTA = 'Trascrizione pronta: associa le voci e genera il verbale';

/**
 * Il foglio non si ridisegna a ogni scatto del cronometro della registrazione (5 volte al secondo
 * per ore): le sue props sono stabili (callback memorizzate, azioni rapide costanti).
 */
const EditorFoglio = React.memo(RichEditor);
/** Pannello «AI» del foglio su un verbale già pubblicato (senza salvataggio automatico). */
const AVVISO_AI_PUBBLICATO = 'Il verbale è pubblicato: le modifiche saranno visibili ai lettori quando salvi';
/** Stesso motivo: i riquadri della colonna destra con props stabili. */
const TrascrizioneMemo = React.memo(VerbaleTrascrizione);
const AssistenteMemo = React.memo(VerbaleAssistente);
const DettagliMemo = React.memo(VerbaleDettagli);
const AllegatiMemo = React.memo(VerbaleAllegati);
const AudioCardMemo = React.memo(VerbaleAudioCard);
/** Foto e post social (piano 3): stesse regole, gestori stabili. */
const FotoMemo = React.memo(VerbaleFoto);
const SocialMemo = React.memo(VerbaleSocial);

type Scheda = 'foglio' | 'assistente' | 'audio' | 'dettagli';

const SCHEDE: { chiave: Scheda; etichetta: string }[] = [
  { chiave: 'foglio', etichetta: 'Foglio' },
  { chiave: 'assistente', etichetta: 'Assistente' },
  { chiave: 'dettagli', etichetta: 'Dettagli e pubblicazione' },
];

/** Sotto `lg` con l'audio: una scheda in più «Audio e trascrizione». */
const SCHEDE_CON_AUDIO: { chiave: Scheda; etichetta: string }[] = [
  SCHEDE[0],
  SCHEDE[1],
  { chiave: 'audio', etichetta: 'Audio e trascrizione' },
  SCHEDE[2],
];

/** Testo semplice di un frammento HTML, senza eseguire nulla (DOMParser non carica immagini né script). */
const testoSemplice = (html: string): string => {
  try {
    const doc = new DOMParser().parseFromString(html || '', 'text/html');
    return (doc.body.textContent || '').replace(/[\s ]+/g, ' ').trim();
  } catch {
    return '';
  }
};

/** Punti dell'ordine del giorno dal verbale (sezione «Ordine del giorno»), per «Segna momento». */
export function puntiOrdineDelGiorno(contenuto: string): string[] {
  const sezione = dividiInSezioni(contenuto).find((s) => s.chiave === 'ordine_del_giorno');
  if (!sezione) return [];
  const blocchi = (re: RegExp) => Array.from(sezione.html.matchAll(re)).map((m) => testoSemplice(m[1]));
  const voci = blocchi(/<li\b[^>]*>([\s\S]*?)<\/li\s*>/gi);
  const righe = voci.length ? voci : blocchi(/<p\b[^>]*>([\s\S]*?)<\/p\s*>/gi);
  return righe
    .map((r) => r.replace(/^\s*\d+\s*[.):\-–]\s*/, '').trim())
    .filter(Boolean)
    .slice(0, 30);
}

interface ConRegistrazioneProps {
  eventId: string;
  /** Un audio è stato avviato, ripreso o chiuso: si rilegge l'elenco. */
  onAudioCambiato: () => void;
  /** Registra, è in pausa o sta chiudendo (blocco dell'uscita dalla pagina). */
  onAttiva: (attiva: boolean) => void;
  children: (registrazione: RegistrazioneVerbale) => React.ReactNode;
}

/**
 * Il registratore (Task 12) vive solo per l'autore che può registrare: lettori e autori senza il
 * permesso non aprono l'archivio locale né leggono gli audio. Ridisegna i figli a ogni scatto.
 */
const ConRegistrazione: React.FC<ConRegistrazioneProps> = ({ eventId, onAudioCambiato, onAttiva, children }) => {
  const registrazione = useRegistrazioneVerbale(eventId);
  const attiva = registrazioneInCorso(registrazione);
  const cambiatoRef = useRef(onAudioCambiato);
  cambiatoRef.current = onAudioCambiato;
  const attivaRef = useRef(onAttiva);
  attivaRef.current = onAttiva;
  useEffect(() => {
    if (registrazione.audioId || registrazione.audioTerminato) cambiatoRef.current();
  }, [registrazione.audioId, registrazione.audioTerminato]);
  useEffect(() => { attivaRef.current(attiva); }, [attiva]);
  useEffect(() => () => attivaRef.current(false), []);
  return <>{children(registrazione)}</>;
};

interface EventoVerbale {
  _id: string;
  nome: string;
  tipologiaId?: { nome?: string } | null;
  dataEvento?: string;
  oraInizio?: string;
  oraFine?: string;
  luogo?: string;
  authorRepresentativeId?: { nome?: string; cognome?: string } | null;
  stats?: { total?: number; checkedIn?: number };
  /** Punti dell'ordine del giorno: assente negli eventi di prima, [] = nessun ordine del giorno. */
  ordineDelGiorno?: string[];
}

interface DatiVerbale {
  bozza: boolean;
  pubblicatoAt: string | null;
  isOwner: boolean;
  allegati: AllegatoVerbale[];
  sezioniRiviste: string[];
  partecipantiEsterni: PartecipanteEsterno[];
  firme: FirmeVerbale;
  /** Autore con il permesso di registrare (piano 2): accende l'audio. */
  puoRegistrare: boolean;
}

const stringhe = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);

const partecipantiDa = (v: unknown): PartecipanteEsterno[] => (Array.isArray(v) ? v : [])
  .filter((p: any) => p && typeof p.nome === 'string')
  .map((p: any) => (typeof p.ruolo === 'string' && p.ruolo ? { nome: p.nome, ruolo: p.ruolo } : { nome: p.nome }));

const firmeDa = (v: unknown): FirmeVerbale => {
  const f = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  return {
    presidente: typeof f.presidente === 'string' ? f.presidente : '',
    segretario: typeof f.segretario === 'string' ? f.segretario : '',
  };
};

/** true se l'HTML ha testo visibile o un'immagine. */
const haTesto = (html: string) =>
  /<img\b/i.test(html || '') || (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;|&#160;/gi, ' ').trim() !== '';

const ora = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
};

const AssociativeVerbale: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [caricamento, setCaricamento] = useState(true);
  const [erroreCaricamento, setErroreCaricamento] = useState('');
  /** 404 dal backend (evento o verbale fuori dalla visibilità): si torna al calendario. */
  const [nonTrovato, setNonTrovato] = useState(false);
  const [evento, setEvento] = useState<EventoVerbale | null>(null);
  const [dati, setDati] = useState<DatiVerbale | null>(null);
  const [profilo, setProfilo] = useState<{ nome?: string; cognome?: string; caricaPrincipale?: string; tipo?: 'interno' | 'esterno'; foto?: string }>({});

  const [contenuto, setContenuto] = useState('');
  const [modificato, setModificato] = useState(false);
  const [salvataggio, setSalvataggio] = useState(false);
  const [erroreSalvataggio, setErroreSalvataggio] = useState(false);
  const [ultimaModifica, setUltimaModifica] = useState<string | null>(null);
  const [mostraAvvio, setMostraAvvio] = useState(false);
  const [scheda, setScheda] = useState<Scheda>('foglio');
  const [pdfInCorso, setPdfInCorso] = useState(false);
  /** Impaginazione in corso sul server (da GET /verbale o dopo «Interrompi»): foglio bloccato. */
  const [impaginazioneInCorso, setImpaginazioneInCorso] = useState(false);
  const [finestraImpagina, setFinestraImpagina] = useState(false);
  const [avvisoIncompleto, setAvvisoIncompleto] = useState(false);

  // -- Audio (piano 2) --
  const [datiAudio, setDatiAudio] = useState<DatiAudioVerbale | null>(null);
  /** Riferimenti di tempo del verbale generato dalla trascrizione (paragrafo ⇒ punto dell'audio). */
  const [riferimenti, setRiferimenti] = useState<RiferimentoVerbale[]>([]);
  /** Da `GET /voci`: voci certe e voci ancora senza persona (controllo finale, tappe). */
  const [infoVoci, setInfoVoci] = useState<{ riconosciute: number; daAssociare: number } | null>(null);
  /** Cresce a ogni salvataggio delle voci: trascrizione e conteggi si rileggono con i nomi nuovi. */
  const [versioneVoci, setVersioneVoci] = useState(0);
  const [vociAperte, setVociAperte] = useState(false);
  const [consensoAperto, setConsensoAperto] = useState(false);
  /** Promemoria già confermato in questa visita: il primo «Carica audio» non lo richiede. */
  const [consensoDato, setConsensoDato] = useState(false);
  const [schedaLaterale, setSchedaLaterale] = useState<'assistente' | 'trascrizione'>('assistente');
  /** Punto da ascoltare nella trascrizione: un oggetto nuovo per ogni clic su «Ascolta da …». */
  const [tempo, setTempo] = useState<{ audioId: string; ms: number } | null>(null);
  /** Riferimento del paragrafo sotto il cursore. */
  const [ascolto, setAscolto] = useState<{ audioId: string; daMs: number } | null>(null);
  const [registrazioneAttiva, setRegistrazioneAttiva] = useState(false);
  /** Uscita dalla pagina in attesa di conferma mentre si registra (azione da eseguire). */
  const [uscita, setUscita] = useState<(() => void) | null>(null);
  /** «Genera verbale dalla trascrizione» in corso in questa pagina: foglio bloccato. */
  const [generazioneInCorso, setGenerazioneInCorso] = useState(false);

  // -- Foto e post social (piano 3): solo per l'autore --
  const [social, setSocial] = useState<StatoSocial | null>(null);
  const [foto, setFoto] = useState<FotoVerbale[]>([]);
  /** Id delle foto per il post: con foto diverse il consenso cade (stesso array finché non cambiano). */
  const chiaveFoto = foto.map((f) => f._id).join(',');
  const fotoIds = useMemo(() => (chiaveFoto ? chiaveFoto.split(',') : []), [chiaveFoto]);
  /** Verbale appena pubblicato: il percorso del post si apre da solo (se il post non è mai stato inviato). */
  const [apriSocial, setApriSocial] = useState(false);

  const contenutoRef = useRef('');
  const salvatoRef = useRef('');
  /** Coda dei salvataggi: ognuno parte solo a fine del precedente e rilegge l'ultimo testo. */
  const codaRef = useRef<Promise<unknown>>(Promise.resolve());
  const idRef = useRef(id);
  idRef.current = id;
  /** Spunte «rivista»: elenco attuale e ultimo elenco salvato (per tornare indietro se il salvataggio fallisce). */
  const rivisteRef = useRef<string[]>([]);
  const rivisteSalvateRef = useRef<string[]>([]);
  const foglioRef = useRef<HTMLDivElement>(null);
  const caricaAudioRef = useRef<VerbaleCaricaAudioHandle>(null);
  const pannelloAudioRef = useRef<HTMLDivElement>(null);
  /** Stato di ogni audio all'ultima lettura (per gli avvisi «trascrizione pronta» / errore). */
  const statiAudioRef = useRef<Map<string, string> | null>(null);
  const letturaAudioRef = useRef(false);
  const rileggiAudioRef = useRef(false);
  /** Dopo «trascrizione pronta»: aprire le voci se ce ne sono da associare. */
  const apriVociRef = useRef(false);
  /** Il clic d'uscita confermato si ripete senza essere fermato di nuovo. */
  const uscitaPermessaRef = useRef(false);
  /** Foglio bloccato (impaginazione o generazione in corso), letto dalle callback stabili. */
  const foglioBloccatoRef = useRef(false);
  foglioBloccatoRef.current = impaginazioneInCorso || generazioneInCorso;

  useEffect(() => {
    try {
      const salvato = localStorage.getItem('representative_profile');
      if (salvato) setProfilo(JSON.parse(salvato));
    } catch { /* profilo non leggibile: shell senza nome */ }
  }, []);

  // -- Caricamento --
  useEffect(() => {
    if (!id) return;
    let attivo = true;
    (async () => {
      setCaricamento(true);
      try {
        const [resEvento, resVerbale] = await Promise.all([getRepEvent(id), getRepEventVerbale(id)]);
        if (!attivo) return;
        const v = resVerbale?.data || {};
        // Elenco di consentiti prima del foglio: il testo può averlo scritto un altro autore
        // (evento riassegnato dall'ufficio) e il foglio lo mette in innerHTML.
        const testo = typeof v.contenuto === 'string' ? sanitizeHtml(v.contenuto) : '';
        setEvento(resEvento?.data || null);
        setDati({
          bozza: v.bozza !== false,
          pubblicatoAt: v.pubblicatoAt || null,
          isOwner: !!v.isOwner,
          allegati: Array.isArray(v.allegati) ? v.allegati : [],
          sezioniRiviste: stringhe(v.sezioniRiviste),
          partecipantiEsterni: partecipantiDa(v.partecipantiEsterni),
          firme: firmeDa(v.firme),
          puoRegistrare: !!v.isOwner && v.puoRegistrare === true,
        });
        setRiferimenti(riferimentiDa(v.riferimenti));
        setContenuto(testo);
        contenutoRef.current = testo;
        salvatoRef.current = testo;
        setUltimaModifica(v.ultimaModifica || null);
        rivisteRef.current = stringhe(v.sezioniRiviste);
        rivisteSalvateRef.current = rivisteRef.current;
        setMostraAvvio(!!v.isOwner && !haTesto(testo));
        setImpaginazioneInCorso(!!v.isOwner && v.impaginazioneInCorso === true);
      } catch (err: any) {
        if (!attivo) return;
        if (err?.response?.status === 401) {
          removeRepresentativeToken();
          navigate('/accesso-associati');
          return;
        }
        if (err?.response?.status === 404) {
          setNonTrovato(true);
          setErroreCaricamento('Verbale non disponibile');
          return;
        }
        setErroreCaricamento(err?.response?.data?.error || 'Verbale non disponibile');
      } finally {
        if (attivo) setCaricamento(false);
      }
    })();
    return () => { attivo = false; };
  }, [id, navigate]);

  // Foto e post social: letti all'apertura solo dall'autore. Senza risposta (rete, backend vecchio)
  // né card foto né casella del post.
  const autoreCaricato = !!dati?.isOwner;
  useEffect(() => {
    if (!id || !autoreCaricato) return undefined;
    let attivo = true;
    (async () => {
      const [statoLetto, fotoLette] = await Promise.allSettled([
        (async () => leggiSocialVerbale(id))(),
        (async () => elencaFotoVerbale(id))(),
      ]);
      if (!attivo) return;
      if (statoLetto.status === 'fulfilled' && statoLetto.value) setSocial(statoLetto.value);
      if (fotoLette.status === 'fulfilled' && Array.isArray(fotoLette.value)) setFoto(fotoLette.value);
    })();
    return () => { attivo = false; };
  }, [id, autoreCaricato]);

  const cambiaSocial = useCallback((s: StatoSocial) => setSocial(s), []);
  const cambiaFoto = useCallback((f: FotoVerbale[]) => setFoto(f), []);
  /** Il caricamento delle foto vive nel percorso del post social (passo «foto»). */
  const socialDisponibileAutore = !!social?.disponibile;
  const contenutoFotoSocial = useMemo(
    () => (id ? <FotoMemo eventId={id} foto={foto} onCambia={cambiaFoto} caricabile={socialDisponibileAutore} /> : null),
    [id, foto, cambiaFoto, socialDisponibileAutore]
  );

  // -- Salvataggio: in coda stretta, uno alla volta; ognuno salva l'ultimo testo (o nulla se è già salvato) --
  const salva = useCallback((): Promise<boolean> => {
    if (!id) return Promise.resolve(false);
    const esegui = async (): Promise<boolean> => {
      const testo = contenutoRef.current;
      if (testo === salvatoRef.current) {
        setModificato(false);
        return true;
      }
      setSalvataggio(true);
      try {
        const res = await saveRepEventVerbale(id, testo);
        salvatoRef.current = testo;
        setUltimaModifica(res?.ultimaModifica || new Date().toISOString());
        setErroreSalvataggio(false);
        return true;
      } catch {
        setErroreSalvataggio(true);
        return false;
      } finally {
        setSalvataggio(false);
        setModificato(contenutoRef.current !== salvatoRef.current);
      }
    };
    const lavoro = codaRef.current.then(esegui);
    codaRef.current = lavoro.catch(() => undefined);
    return lavoro;
  }, [id]);

  const salvaRef = useRef(salva);
  salvaRef.current = salva;

  // Salvataggio automatico 3 s dopo l'ultima modifica, solo in bozza: sul verbale pubblicato
  // ogni pausa manderebbe agli invitati un testo a metà e azzererebbe le loro letture.
  const autosalvataggio = dati?.bozza !== false;
  useEffect(() => {
    if (!modificato || !autosalvataggio) return undefined;
    const timer = setTimeout(() => { salvaRef.current(); }, RITARDO_SALVATAGGIO);
    return () => clearTimeout(timer);
  }, [contenuto, modificato, autosalvataggio]);

  // Uscita dalla pagina (link interni dell'area): beforeunload non scatta e il timer si
  // annulla, quindi le modifiche in sospeso si salvano senza aspettare la risposta. Il PUT
  // va in coda dietro a quello in corso (la promessa sopravvive allo smontaggio): niente due
  // PUT in parallelo e niente testo vecchio che arriva dopo quello nuovo.
  useEffect(() => () => {
    const eventoId = idRef.current;
    if (!eventoId) return;
    codaRef.current = codaRef.current.then(() => {
      const testo = contenutoRef.current;
      if (testo === salvatoRef.current) return undefined;
      return saveRepEventVerbale(eventoId, testo).catch(() => { /* pagina già chiusa: nulla da mostrare */ });
    });
  }, []);

  // Avviso se si chiude la scheda con modifiche non salvate
  useEffect(() => {
    if (!modificato) return undefined;
    const avviso = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', avviso);
    return () => window.removeEventListener('beforeunload', avviso);
  }, [modificato]);

  const cambiaContenuto = useCallback((html: string) => {
    contenutoRef.current = html;
    setContenuto(html);
    setModificato(html !== salvatoRef.current);
  }, []);

  /** Contenuto già salvato dal server (impaginazione, ripristino), ripulito prima del foglio. */
  const contenutoDalServer = useCallback((html: string, quando: string | null) => {
    const pulito = sanitizeHtml(html);
    contenutoRef.current = pulito;
    salvatoRef.current = pulito;
    setContenuto(pulito);
    setModificato(false);
    setErroreSalvataggio(false);
    setUltimaModifica(quando || new Date().toISOString());
  }, []);

  /** Impaginazione finita (dalla finestra): contenuto già salvato dal server. */
  const impaginato = useCallback((html: string, quando: string | null, troncato: boolean) => {
    contenutoDalServer(html, quando);
    setMostraAvvio(false);
    setFinestraImpagina(false);
    setAvvisoIncompleto(troncato);
  }, [contenutoDalServer]);

  // Impaginazione in corso sul server: ogni 10 s si rilegge il verbale; alla fine il foglio
  // si sblocca con il contenuto salvato dal server.
  useEffect(() => {
    if (!impaginazioneInCorso || !id) return undefined;
    let attivo = true;
    let inVolo = false;
    const timer = setInterval(async () => {
      if (inVolo) return;
      inVolo = true;
      try {
        const res = await getRepEventVerbale(id);
        if (!attivo) return;
        const v = res?.data || {};
        if (v.impaginazioneInCorso === true) return;
        const testo = typeof v.contenuto === 'string' ? sanitizeHtml(v.contenuto) : '';
        const cambiato = testo !== salvatoRef.current;
        contenutoDalServer(testo, v.ultimaModifica || null);
        setRiferimenti(riferimentiDa(v.riferimenti));
        setImpaginazioneInCorso(false);
        if (haTesto(testo)) setMostraAvvio(false);
        if (cambiato) toast.success('Verbale aggiornato: il foglio mostra il nuovo testo');
        else toast.info('Generazione terminata', 'Il verbale non è cambiato: se serve, riprova.');
      } catch {
        /* rete o server momentaneamente giù: si riprova al giro dopo */
      } finally {
        inVolo = false;
      }
    }, INTERVALLO_CONTROLLO_IMPAGINAZIONE);
    return () => { attivo = false; clearInterval(timer); };
  }, [impaginazioneInCorso, id, contenutoDalServer, toast]);

  const salvaOra = async () => {
    const ok = await salva();
    if (ok) toast.success('Verbale salvato');
    else toast.error('Verbale non salvato, riprova');
  };

  const torna = async () => {
    if (contenutoRef.current !== salvatoRef.current && !(await salva())) {
      toast.error('Non sono riuscito a salvare: riprova prima di uscire');
      return;
    }
    navigate(`/area-associativa/calendario/${id}`);
  };

  // -- Sezioni --
  const vaiAllaSezione = (indice: number) => {
    const titoli = foglioRef.current?.querySelectorAll('h2');
    const titolo = titoli?.[indice] as HTMLElement | undefined;
    if (titolo && typeof titolo.scrollIntoView === 'function') titolo.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // -- Segni [da verificare]: evidenziati in giallo nel foglio, «Vai al prossimo» dalla riga della revisione --
  const prossimoSegnoRef = useRef(0);
  useEffect(() => {
    const cornice = requestAnimationFrame(() => {
      evidenziaSegni(trovaSegniDaVerificare(foglioRef.current?.querySelector<HTMLElement>('.verbale-foglio-pagina')));
    });
    return () => cancelAnimationFrame(cornice);
  }, [contenuto, caricamento, scheda]);
  useEffect(() => togliEvidenziazione, []);

  /** Seleziona il prossimo segno (`indiceSezione`: il primo di quella sezione). */
  const vaiAlDaVerificare = (indiceSezione?: number) => {
    const editor = foglioRef.current?.querySelector<HTMLElement>('.verbale-foglio-pagina');
    const segni = trovaSegniDaVerificare(editor);
    if (segni.length === 0) return;
    let scelto: Range | undefined;
    if (typeof indiceSezione === 'number') {
      const titoli = foglioRef.current?.querySelectorAll('h2') || [];
      scelto = segni.find((r) => sezioneDelSegno(r, titoli) === indiceSezione);
    } else {
      const i = prossimoSegnoRef.current % segni.length;
      prossimoSegnoRef.current = i + 1;
      scelto = segni[i];
    }
    if (scelto) selezionaSegno(scelto, editor);
  };

  /** Salva le spunte «rivista» calcolate dall'elenco attuale (un solo PUT dei dettagli). */
  const salvaRiviste = async (calcola: (attuali: string[]) => string[]) => {
    if (!id) return;
    const nuove = calcola(rivisteRef.current);
    rivisteRef.current = nuove;
    setDati((d) => (d ? { ...d, sezioniRiviste: nuove } : d));
    try {
      await salvaDettagliVerbale(id, { sezioniRiviste: nuove });
      rivisteSalvateRef.current = nuove;
    } catch {
      const salvate = rivisteSalvateRef.current;
      rivisteRef.current = salvate;
      setDati((d) => (d ? { ...d, sezioniRiviste: salvate } : d));
      toast.error('Spunta non salvata, riprova');
    }
  };

  const segnaRivista = (chiave: string, rivista: boolean) => salvaRiviste((attuali) => (
    rivista ? Array.from(new Set([...attuali, chiave])) : attuali.filter((c) => c !== chiave)
  ));

  const segnaTutteRiviste = (chiavi: string[], rivista: boolean) => salvaRiviste((attuali) => (
    rivista ? Array.from(new Set([...attuali, ...chiavi])) : attuali.filter((c) => !chiavi.includes(c))
  ));

  /** Partecipanti esterni salvati: si riportano nella sezione «Presenti», come una modifica dell'autore. */
  const cambioDettagli = useCallback((dettagli: { firme?: FirmeVerbale; partecipantiEsterni?: PartecipanteEsterno[] }) => {
    const esterni = dettagli.partecipantiEsterni;
    if (!esterni) return;
    setDati((d) => (d ? { ...d, partecipantiEsterni: esterni } : d));
    if (foglioBloccatoRef.current) return;
    const attuale = contenutoRef.current;
    const aggiornato = aggiornaPartecipantiEsterni(attuale, esterni);
    if (aggiornato !== attuale) {
      cambiaContenuto(aggiornato);
      toast.info('Sezione «Presenti» aggiornata', 'I partecipanti esterni sono stati riportati nel testo.');
    }
  }, [cambiaContenuto, toast]);

  // -- PDF --
  /** Anteprima del PDF dell'autore in una scheda nuova (se il browser la blocca, si scarica). */
  const apriPdf = async () => {
    if (!id || pdfInCorso) return;
    // La finestra si apre subito (dentro il clic), altrimenti il browser la blocca
    const finestra = window.open('', '_blank');
    setPdfInCorso(true);
    try {
      if (!(await salva())) throw new Error('salvataggio');
      const blob = await scaricaPdfVerbale(id);
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      if (finestra) {
        finestra.location.href = url;
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = nomeFilePdfVerbale(evento?.nome || '');
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      finestra?.close();
      toast.error('PDF non disponibile, riprova');
    } finally {
      setPdfInCorso(false);
    }
  };

  // -- Assistente --
  const applicaDalAssistente = useCallback(async (html: string) => {
    cambiaContenuto(sanitizeHtml(html));
    const ok = await salvaRef.current();
    if (ok) toast.success('Proposta applicata al verbale');
    else toast.warning('Proposta applicata', 'Il salvataggio non è riuscito: riprova con «Salva».');
  }, [cambiaContenuto, toast]);

  const aiRefine = useCallback(
    ({ content, instruction }: { content: string; instruction: string }) => (instruction === ISTRUZIONE_RIPRENDI
      ? riprendiDallaTrascrizione(id || '', content)
      : riscriviVerbale(id || '', content, instruction)).then(sanitizeHtml),
    [id]
  );

  const sezioni = useMemo(() => dividiInSezioni(contenuto).filter((s) => s.chiave !== 'preambolo'), [contenuto]);
  const rivisteValide = useMemo(() => {
    const chiavi = new Set(sezioni.map((s) => s.chiave));
    return (dati?.sezioniRiviste || []).filter((c) => chiavi.has(c)).length;
  }, [sezioni, dati?.sezioniRiviste]);

  // -- Audio (piano 2) --
  const audioDisponibile = !!dati?.isOwner && !!dati?.puoRegistrare;
  const leggibili = useMemo(() => audioConTrascrizione(datiAudio?.audio || []), [datiAudio]);
  const haTrascrizione = audioDisponibile && leggibili.length > 0;
  const aiAzioniRapide = haTrascrizione ? AZIONI_RAPIDE_CON_TRASCRIZIONE : AZIONI_RAPIDE_FOGLIO;
  // Punti per «Segna momento»: dal verbale; con il verbale ancora vuoto, dall'ordine del giorno dell'evento.
  const odgEvento = evento?.ordineDelGiorno;
  const odg = useMemo(() => {
    const dalVerbale = puntiOrdineDelGiorno(contenuto);
    if (dalVerbale.length || !Array.isArray(odgEvento)) return dalVerbale;
    return odgEvento.filter((p) => typeof p === 'string' && p.trim()).slice(0, 30);
  }, [contenuto, odgEvento]);

  /** Rilegge gli audio; avvisa quando uno diventa trascritto (o va in errore) durante la visita. */
  const leggiAudio = useCallback(async (): Promise<void> => {
    const eventoId = idRef.current;
    if (!eventoId) return;
    // Una lettura alla volta; una richiesta arrivata nel frattempo (es. dopo «Termina») si rifà subito dopo.
    if (letturaAudioRef.current) {
      rileggiAudioRef.current = true;
      return;
    }
    letturaAudioRef.current = true;
    rileggiAudioRef.current = false;
    try {
      const d = await elencaAudio(eventoId);
      if (idRef.current !== eventoId || !d || !Array.isArray(d.audio)) return;
      const prima = statiAudioRef.current;
      if (prima) {
        let pronta = false;
        d.audio.forEach((a) => {
          const precedente = prima.get(String(a._id));
          if (!precedente || precedente === a.stato) return;
          if (a.stato === 'trascritta') pronta = true;
          else if (a.stato === 'errore') toast.error(a.errore?.messaggio || 'Trascrizione non riuscita: riprova');
        });
        if (pronta) {
          toast.success(TESTO_TRASCRIZIONE_PRONTA);
          apriVociRef.current = true;
        }
      }
      statiAudioRef.current = new Map(d.audio.map((a) => [String(a._id), a.stato]));
      setDatiAudio(d);
    } catch {
      /* rete o server momentaneamente giù: si riprova al giro dopo */
    } finally {
      letturaAudioRef.current = false;
    }
    if (rileggiAudioRef.current && idRef.current === eventoId) await leggiAudioRef.current();
  }, [toast]);
  const leggiAudioRef = useRef(leggiAudio);
  leggiAudioRef.current = leggiAudio;

  // Gestori stabili per i riquadri memorizzati (non si ridisegnano a ogni scatto del cronometro).
  const riprovaTrascrizione = useCallback(async (audioId: string) => {
    try {
      await trascriviAudio(idRef.current || '', audioId);
      toast.success('Trascrizione rimessa in coda');
    } catch (err) {
      toast.error(erroreRichiesta(err).messaggio);
    } finally {
      leggiAudio();
    }
  }, [leggiAudio, toast]);

  const eliminaUnAudio = useCallback(async (audioId: string) => {
    try {
      await eliminaAudio(idRef.current || '', audioId);
      setRiferimenti((rs) => rs.filter((r) => r.audioId !== audioId));
      toast.success('Audio eliminato');
    } catch (err) {
      toast.error(erroreRichiesta(err).messaggio);
    } finally {
      leggiAudio();
    }
  }, [leggiAudio, toast]);

  const apriVoci = useCallback(() => setVociAperte(true), []);
  const cambioAllegati = useCallback(
    (allegati: AllegatoVerbale[]) => setDati((d) => (d ? { ...d, allegati } : d)),
    []
  );

  useEffect(() => {
    if (audioDisponibile) leggiAudio();
  }, [audioDisponibile, leggiAudio]);

  // Un audio in coda o in trascrizione: si rilegge ogni 10 s finché non finisce.
  const audioDaSeguire = !!datiAudio?.audio.some((a) => a.stato === 'in_trascrizione' || a.stato === 'completa');
  useEffect(() => {
    if (!audioDisponibile || !audioDaSeguire) return undefined;
    const timer = setInterval(() => { leggiAudio(); }, INTERVALLO_CONTROLLO_AUDIO);
    return () => clearInterval(timer);
  }, [audioDisponibile, audioDaSeguire, leggiAudio]);

  // Voci delle trascrizioni: riconosciute e da associare (si rileggono dopo il salvataggio delle voci).
  const chiaveTrascritti = (datiAudio?.audio || []).filter((a) => a.stato === 'trascritta').map((a) => a._id).join(',');
  useEffect(() => {
    if (!audioDisponibile || !id || !chiaveTrascritti) {
      setInfoVoci(null);
      return undefined;
    }
    let vivo = true;
    leggiVoci(id)
      .then(({ voci }) => {
        if (!vivo) return;
        const riconosciute = voci.filter((v) => v.certa).length;
        // Senza persona: nessun nome né invito; le incerte «stessa persona di Voce N» sono associate.
        const daAssociare = voci.filter((v) => !(v.nome || '').trim() && !v.invitationId && !(v.uguale && !v.certa)).length;
        setInfoVoci({ riconosciute, daAssociare });
        if (apriVociRef.current) {
          apriVociRef.current = false;
          if (daAssociare > 0) setVociAperte(true);
        }
      })
      .catch(() => { if (vivo) apriVociRef.current = false; });
    return () => { vivo = false; };
  }, [audioDisponibile, id, chiaveTrascritti, versioneVoci]);

  // Cursore in un paragrafo del foglio con un riferimento ⇒ «Ascolta da …».
  const chiaviRef = useRef<string[]>([]);
  chiaviRef.current = sezioni.map((s) => s.chiave);
  const riferimentiRef = useRef<RiferimentoVerbale[]>([]);
  riferimentiRef.current = riferimenti;
  const conRiferimenti = audioDisponibile && riferimenti.length > 0;
  useEffect(() => {
    if (!conRiferimenti) {
      setAscolto(null);
      return undefined;
    }
    const aggiorna = () => {
      const selezione = typeof document.getSelection === 'function' ? document.getSelection() : null;
      const nodo = selezione && selezione.rangeCount > 0 ? selezione.anchorNode : null;
      const paragrafo = paragrafoAlCursore(foglioRef.current, nodo, chiaviRef.current);
      const trovato = paragrafo ? trovaRiferimento(riferimentiRef.current, paragrafo.chiave, paragrafo.html) : null;
      setAscolto((prima) => (prima && trovato && prima.audioId === trovato.audioId && prima.daMs === trovato.daMs ? prima : trovato));
    };
    document.addEventListener('selectionchange', aggiorna);
    return () => document.removeEventListener('selectionchange', aggiorna);
  }, [conRiferimenti]);

  // Mentre si registra, i pulsanti dell'area (menu, «Area riservata», «Esci») chiedono conferma:
  // lasciando la pagina la registrazione si ferma (riprendibile riaprendola). BrowserRouter non ha
  // useBlocker: si ferma il clic prima di React e lo si ripete dopo la conferma.
  useEffect(() => {
    if (!registrazioneAttiva) return undefined;
    const alClic = (e: MouseEvent) => {
      if (uscitaPermessaRef.current) return;
      const bersaglio = e.target as Element | null;
      const el = (bersaglio && typeof bersaglio.closest === 'function' ? bersaglio.closest('a[href], button') : null) as HTMLElement | null;
      if (!el || !el.closest('.assoc-app') || el.closest('main')) return;
      if (el.closest('.hamburger, .side-collapse, [aria-label="Cambia tema"]')) return;
      if (el.tagName === 'A' && (el.getAttribute('target') === '_blank' || el.hasAttribute('download'))) return;
      e.preventDefault();
      e.stopPropagation();
      setUscita(() => () => {
        uscitaPermessaRef.current = true;
        try { el.click(); } finally { uscitaPermessaRef.current = false; }
      });
    };
    document.addEventListener('click', alClic, true);
    return () => document.removeEventListener('click', alClic, true);
  }, [registrazioneAttiva]);

  const datiTestata = evento ? {
    nome: evento.nome,
    tipologia: evento.tipologiaId?.nome || null,
    dataEvento: evento.dataEvento,
    oraInizio: evento.oraInizio,
    oraFine: evento.oraFine,
    luogo: evento.luogo,
    presenti: evento.stats?.checkedIn ?? 0,
    invitati: evento.stats?.total ?? 0,
  } : null;

  const autore = [evento?.authorRepresentativeId?.nome, evento?.authorRepresentativeId?.cognome]
    .filter(Boolean).join(' ').trim();

  const shell = (figli: React.ReactNode) => (
    <AssociativeAppShell
      route="calendario"
      nome={profilo.nome}
      cognome={profilo.cognome}
      caricaPrincipale={profilo.caricaPrincipale}
      tipo={profilo.tipo}
      foto={profilo.foto}
    >
      <div className="mx-auto w-full max-w-[1480px] px-3 py-4 sm:px-5 sm:py-6 lg:px-6">{figli}</div>
    </AssociativeAppShell>
  );

  if (caricamento) {
    return shell(
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-500" aria-label="Caricamento" />
      </div>
    );
  }

  if (erroreCaricamento || !evento || !dati || !datiTestata) {
    return shell(
      <div className="mx-auto max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center dark:border-gray-700 dark:bg-gray-800">
        <AlertCircle className="mx-auto mb-3 h-8 w-8 text-slate-300" aria-hidden="true" />
        <p className="text-sm font-medium text-slate-700 dark:text-gray-200">{erroreCaricamento || 'Verbale non disponibile'}</p>
        <button
          type="button"
          onClick={() => navigate(id && !nonTrovato ? `/area-associativa/calendario/${id}` : '/area-associativa/calendario')}
          className="mt-4 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
        >
          {nonTrovato ? 'Torna al calendario' : "Torna all'evento"}
        </button>
      </div>
    );
  }

  const testata = (
    <VerbaleTestata
      evento={datiTestata}
      bozza={dati.bozza}
      audioDisponibile={false}
      onTorna={torna}
    />
  );

  // -- Lettori (non autori) --
  if (!dati.isOwner) {
    if (dati.bozza) {
      return shell(
        <>
          {testata}
          <div className="mx-auto mt-6 max-w-lg rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <span className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300">
              <FileText className="h-6 w-6" aria-hidden="true" />
            </span>
            <p className="text-base font-semibold text-slate-800 dark:text-gray-100">Il verbale non è ancora disponibile</p>
            <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">Lo trovi qui quando l'autore dell'evento lo pubblica.</p>
          </div>
        </>
      );
    }
    // Pubblicato: la lettura condivisa con associati e admin (Task 5), nient'altro
    const datiLettura: VerbaleLetturaDati = {
      evento: {
        _id: evento._id,
        nome: evento.nome,
        dataEvento: evento.dataEvento,
        oraInizio: evento.oraInizio,
        oraFine: evento.oraFine,
        luogo: evento.luogo,
        tipologia: evento.tipologiaId?.nome ? { nome: evento.tipologiaId.nome } : null,
      },
      verbale: {
        contenuto,
        pubblicatoAt: dati.pubblicatoAt,
        ultimaModifica,
        allegati: dati.allegati,
      },
    };
    return shell(
      <div className="mx-auto max-w-4xl space-y-4">
        <button
          type="button"
          onClick={() => navigate(`/area-associativa/calendario/${evento._id}`)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 dark:text-gray-300 dark:hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Torna all'evento
        </button>
        <VerbaleLettura dati={datiLettura} onScaricaPdf={() => scaricaPdfVerbale(evento._id)} />
      </div>
    );
  }

  // -- Autore --
  const statoSalvataggio = salvataggio ? (
    <span className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-gray-400">
      <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" /> Salvataggio…
    </span>
  ) : erroreSalvataggio ? (
    <span className="text-xs font-medium text-red-600 dark:text-red-400">Non salvato, riprova</span>
  ) : modificato ? (
    <span className="text-xs font-medium text-amber-600 dark:text-amber-400">Modifiche non salvate</span>
  ) : ultimaModifica ? (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
      <CheckCircle2 className="h-3 w-3" aria-hidden="true" /> Salvato alle {ora(ultimaModifica)}
    </span>
  ) : null;

  /** Post social disponibile per l'autore (interruttore, collegamento, permesso). */
  const socialDisponibile = !!social?.disponibile;

  /** Foglio bloccato: impaginazione o generazione in corso (sul server o in questa pagina). */
  const foglioBloccato = impaginazioneInCorso || generazioneInCorso;

  // Niente overflow-hidden sulla carta: la barra dell'editor è sticky e il pannello «AI» ne esce
  const foglio = (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex flex-wrap items-center justify-end gap-2 rounded-t-2xl border-b border-slate-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setFinestraImpagina(true)}
            disabled={foglioBloccato}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            Impagina da un testo…
          </button>
          <button
            type="button"
            onClick={() => { apriPdf(); }}
            disabled={pdfInCorso}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            {pdfInCorso ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Printer className="h-3.5 w-3.5" aria-hidden="true" />}
            Anteprima PDF
          </button>
          <VerbaleVersioni eventId={evento._id} salvaSeServe={salva} onRipristinata={contenutoDalServer} disabled={foglioBloccato} />
          {!dati.bozza && (
            <span className="rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-800 dark:bg-amber-900/25 dark:text-amber-200">
              Verbale pubblicato: gli invitati vedono le modifiche quando salvi
            </span>
          )}
          <span className="min-w-[7rem] text-right" aria-live="polite">{statoSalvataggio}</span>
          <button
            type="button"
            onClick={salvaOra}
            disabled={salvataggio || !modificato || foglioBloccato}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-40"
          >
            <Save className="h-3.5 w-3.5" aria-hidden="true" />
            Salva
          </button>
        </div>
      </div>
      <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-900/40">
        <VerbaleSezioni
          contenuto={contenuto}
          sezioniRiviste={dati.sezioniRiviste}
          onVai={vaiAllaSezione}
          onSegna={segnaRivista}
          onSegnaTutte={segnaTutteRiviste}
          onVaiDaVerificare={vaiAlDaVerificare}
        />
      </div>
      <div ref={foglioRef} className="verbale-foglio rounded-b-2xl">
        <EditorFoglio
          value={contenuto}
          onChange={cambiaContenuto}
          disabled={foglioBloccato}
          height={720}
          compact
          hideMedia
          placeholder="Scrivi il verbale…"
          aiRefine={aiRefine}
          aiAzioniRapide={aiAzioniRapide}
          aiAvviso={dati.bozza ? undefined : AVVISO_AI_PUBBLICATO}
          editorClassName="verbale-foglio-pagina font-serif text-base leading-relaxed"
        />
      </div>
    </div>
  );

  // -- Audio: azioni della pagina --
  const lato = haTrascrizione ? schedaLaterale : 'assistente';
  const haAudio = !!datiAudio && datiAudio.audio.length > 0;
  const audioAscolto = ascolto ? leggibili.find((a) => a._id === ascolto.audioId) || null : null;

  /** Porta in vista il riquadro dell'audio (sotto `lg` apre la sua scheda). */
  const mostraAudio = () => {
    setScheda('audio');
    setTimeout(() => {
      const el = pannelloAudioRef.current;
      if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const registra = (r: RegistrazioneVerbale | null) => {
    if (!r) return;
    // Già in corso, interrotta o aperta in un'altra scheda: il riquadro dice cosa fare.
    if (registrazioneInCorso(r) || r.interrotta || r.altraScheda) {
      mostraAudio();
      return;
    }
    setConsensoAperto(true);
  };

  const caricaAudio = () => {
    setScheda('audio');
    caricaAudioRef.current?.scegliFile();
  };

  const ascoltaDa = (punto: { audioId: string; daMs: number }) => {
    setTempo({ audioId: punto.audioId, ms: punto.daMs });
    setSchedaLaterale('trascrizione');
    setScheda('audio');
  };


  /** Verbale generato dalla trascrizione: il server l'ha già salvato (con una versione di prima). */
  const generato = (d: FineGenerazione) => {
    if (typeof d?.contenuto === 'string') contenutoDalServer(d.contenuto, d.ultimaModifica || null);
    setRiferimenti(riferimentiDa(d?.riferimenti));
    setMostraAvvio(false);
    setAvvisoIncompleto(false);
    setScheda('foglio');
  };

  const genera = (
    <VerbaleGenera
      eventId={evento._id}
      haContenuto={haTesto(contenuto)}
      onFine={generato}
      primaDiGenerare={salva}
      onInCorso={setGenerazioneInCorso}
      onSulServer={() => setImpaginazioneInCorso(true)}
      disabilitato={impaginazioneInCorso}
      pubblicato={!dati.bozza}
    />
  );

  const vistaAutore = (r: RegistrazioneVerbale | null) => {
    const inCorso = !!r && registrazioneInCorso(r);
    const schede = audioDisponibile ? SCHEDE_CON_AUDIO : SCHEDE;
    return (
      <>
        <VerbaleTestata
          evento={datiTestata}
          bozza={dati.bozza}
          audioDisponibile={audioDisponibile}
          onTorna={() => (inCorso ? setUscita(() => () => { torna(); }) : torna())}
          onRegistra={() => registra(r)}
          onCaricaAudio={caricaAudio}
          inRegistrazione={r && (r.stato === 'registra' || r.stato === 'pausa') ? r.msRipresa : null}
          inPausa={r?.stato === 'pausa'}
          videoGuida
        />
        {impaginazioneInCorso && (
          <div role="status" className="mt-4 flex items-start gap-2 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900 dark:border-sky-800 dark:bg-sky-900/20 dark:text-sky-100">
            <Loader2 className="mt-0.5 h-4 w-4 flex-shrink-0 animate-spin" aria-hidden="true" />
            <span>{TESTO_GENERAZIONE_IN_CORSO_PAGINA}</span>
          </div>
        )}
        {avvisoIncompleto && !impaginazioneInCorso && (
          <div role="alert" className="mt-4 flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-100">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span className="flex-1">Il verbale potrebbe essere incompleto: controlla la fine</span>
            <button
              type="button"
              onClick={() => setAvvisoIncompleto(false)}
              aria-label="Chiudi l'avviso"
              className="rounded p-0.5 text-amber-700 hover:bg-amber-100 dark:text-amber-200 dark:hover:bg-amber-900/40"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        )}
        <div className="mt-4">
          <VerbaleTappe
            haContenuto={haTesto(contenuto)}
            sezioniTotali={sezioni.length}
            sezioniRiviste={rivisteValide}
            bozza={dati.bozza}
            pubblicatoAt={dati.pubblicatoAt}
            audioUsato={false}
            audio={audioDisponibile ? datiAudio : null}
            registrazioneAttiva={inCorso}
            registrazioneInterrotta={!!r?.interrotta && !r.altraScheda}
            vociRiconosciute={infoVoci?.riconosciute ?? null}
          />
        </div>

        <div className="mt-4 flex gap-1 rounded-xl bg-slate-100 p-1 dark:bg-gray-800 lg:hidden" role="group" aria-label="Schede del verbale">
          {schede.map((s) => (
            <button
              key={s.chiave}
              type="button"
              onClick={() => setScheda(s.chiave)}
              aria-pressed={scheda === s.chiave}
              className={`flex-1 rounded-lg px-2 py-2 text-sm font-medium transition-colors ${
                scheda === s.chiave
                  ? 'bg-white text-emerald-800 shadow-sm dark:bg-gray-700 dark:text-emerald-200'
                  : 'text-slate-600 hover:text-slate-900 dark:text-gray-300'
              }`}
            >
              {s.etichetta}
            </button>
          ))}
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-[7fr_3fr] lg:items-start">
          <div className={`${scheda === 'foglio' ? 'block' : 'hidden'} min-w-0 lg:block`}>
            {mostraAvvio ? (
              <VerbaleAvvio
                eventId={evento._id}
                audioDisponibile={audioDisponibile}
                bloccato={foglioBloccato}
                onScriviDaZero={() => {
                  // Con l'ordine del giorno dell'evento la sezione parte già con i punti; senza, non c'è.
                  const odg = Array.isArray(evento.ordineDelGiorno) ? evento.ordineDelGiorno : null;
                  cambiaContenuto(scheletroVerbale({ puntiOdg: odg, senzaOdg: !!odg && odg.length === 0 }));
                  setMostraAvvio(false);
                }}
                onImpaginato={impaginato}
                onInCorsoSulServer={() => setImpaginazioneInCorso(true)}
                onRegistra={() => registra(r)}
                onCaricaAudio={caricaAudio}
                genera={haTrascrizione ? genera : undefined}
              />
            ) : foglio}
          </div>
          {finestraImpagina && (
            <FinestraImpagina
              eventId={evento._id}
              sostituisce={haTesto(contenuto)}
              primaDiImpaginare={salva}
              onChiudi={() => setFinestraImpagina(false)}
              onImpaginato={impaginato}
              onInCorsoSulServer={() => setImpaginazioneInCorso(true)}
              pubblicato={!dati.bozza}
            />
          )}

          <aside className={`${scheda === 'foglio' ? 'hidden' : 'flex'} min-w-0 flex-col gap-4 lg:flex`}>
            {haTrascrizione && (
              <div className="hidden gap-1 rounded-xl bg-slate-100 p-1 dark:bg-gray-900/60 lg:flex" role="group" aria-label="Colonna laterale">
                {([['assistente', 'Assistente'], ['trascrizione', 'Trascrizione']] as const).map(([chiave, etichetta]) => (
                  <button
                    key={chiave}
                    type="button"
                    onClick={() => setSchedaLaterale(chiave)}
                    aria-pressed={lato === chiave}
                    className={`flex-1 rounded-lg px-2 py-1.5 text-sm font-medium transition-colors ${
                      lato === chiave
                        ? 'bg-white text-emerald-800 shadow-sm dark:bg-gray-700 dark:text-emerald-200'
                        : 'text-slate-600 hover:text-slate-900 dark:text-gray-300'
                    }`}
                  >
                    {etichetta}
                  </button>
                ))}
              </div>
            )}
            <div className={`${scheda === 'assistente' ? 'block' : 'hidden'} ${lato === 'assistente' ? 'lg:block' : 'lg:hidden'}`}>
              <AssistenteMemo
                eventId={evento._id}
                contenuto={contenuto}
                onApplica={applicaDalAssistente}
                primaDiApplicare={salva}
                bloccato={foglioBloccato}
              />
            </div>
            {haTrascrizione && datiAudio && (
              <div className={`${scheda === 'audio' ? 'block' : 'hidden'} ${lato === 'trascrizione' ? 'lg:block' : 'lg:hidden'}`}>
                <TrascrizioneMemo eventId={evento._id} audio={datiAudio.audio} tempo={tempo} versioneVoci={versioneVoci} />
              </div>
            )}
            {audioDisponibile && (
              <div ref={pannelloAudioRef} className={`${scheda === 'audio' ? 'flex' : 'hidden'} scroll-mt-4 flex-col gap-4 lg:flex`}>
                {r && <VerbaleRegistrazione registrazione={r} odg={odg} />}
                {haAudio && datiAudio && (
                  <AudioCardMemo
                    dati={datiAudio}
                    onRiprova={riprovaTrascrizione}
                    onElimina={eliminaUnAudio}
                    onApriVoci={apriVoci}
                    audioInRegistrazione={inCorso && r ? r.audioId : null}
                  />
                )}
                {haTrascrizione && !mostraAvvio && (
                  <section aria-label="Verbale dalla trascrizione" className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">Verbale dalla trascrizione</p>
                    <p className="mb-3 mt-1 text-xs text-slate-500 dark:text-gray-400">
                      L'AI riscrive tutte le sezioni dalla trascrizione e dalle voci associate; il verbale attuale resta tra le versioni.
                    </p>
                    {genera}
                  </section>
                )}
                {!haAudio && !inCorso && (
                  <section aria-label="Registra la riunione" className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                    <p className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white">
                      <Mic className="h-4 w-4 text-red-600 dark:text-red-400" aria-hidden="true" />
                      Registra la riunione
                    </p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-gray-400">
                      Il microfono del computer registra la riunione; alla fine la trascrizione parte da sola e ti avvisiamo quando è pronta.
                    </p>
                    <button
                      type="button"
                      onClick={() => registra(r)}
                      className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
                    >
                      <Mic className="h-4 w-4" aria-hidden="true" />
                      Avvia la registrazione
                    </button>
                  </section>
                )}
                <VerbaleCaricaAudio
                  ref={caricaAudioRef}
                  eventId={evento._id}
                  consensoGiaDato={consensoDato}
                  onConsensoDato={() => setConsensoDato(true)}
                  onCaricato={() => { leggiAudio(); }}
                />
              </div>
            )}
            <div className={`${scheda === 'dettagli' ? 'flex' : 'hidden'} flex-col gap-4 lg:flex`}>
              <DettagliMemo
                eventId={evento._id}
                firme={dati.firme}
                partecipantiEsterni={dati.partecipantiEsterni}
                presidentePredefinito={autore}
                onCambio={cambioDettagli}
              />
              <AllegatiMemo
                eventId={evento._id}
                allegati={dati.allegati}
                onCambio={cambioAllegati}
              />
              {/* Post non disponibile ma foto rimaste da prima: si possono ancora vedere ed eliminare. */}
              {!socialDisponibile && foto.length > 0 && (
                <FotoMemo
                  eventId={evento._id}
                  foto={foto}
                  onCambia={cambiaFoto}
                  caricabile={false}
                />
              )}
              {/* Il post social arriva dopo la pubblicazione: prima nella pagina non c'è. */}
              {socialDisponibile && social && (!dati.bozza || !!social.postpickr) && (
                <SocialMemo
                  eventId={evento._id}
                  stato={social}
                  numeroFoto={foto.length}
                  fotoIds={fotoIds}
                  onCambia={cambiaSocial}
                  apriAllaPubblicazione={apriSocial}
                  contenutoFoto={contenutoFotoSocial}
                />
              )}
              <VerbalePubblica
                eventId={evento._id}
                bozza={dati.bozza}
                pubblicatoAt={dati.pubblicatoAt}
                contenuto={contenuto}
                sezioniRiviste={dati.sezioniRiviste}
                allegati={dati.allegati}
                invitati={datiTestata.invitati}
                vociDaAssociare={audioDisponibile ? infoVoci?.daAssociare || 0 : 0}
                partecipantiEsterni={dati.partecipantiEsterni}
                salvaSeServe={salva}
                bloccato={foglioBloccato}
                onPubblicato={(quando) => {
                  if (dati.bozza) setApriSocial(true);
                  setDati((d) => (d ? { ...d, bozza: false, pubblicatoAt: d.pubblicatoAt || quando } : d));
                }}
                onRitirato={() => {
                  setApriSocial(false);
                  setDati((d) => (d ? { ...d, bozza: true, pubblicatoAt: null } : d));
                }}
              />
            </div>
          </aside>
        </div>

        {audioAscolto && ascolto && !mostraAvvio && (
          <div className={`${scheda === 'foglio' ? 'flex' : 'hidden'} pointer-events-none fixed inset-x-0 bottom-5 z-[60] justify-center px-4 lg:flex`}>
            <button
              type="button"
              // Il cursore resta nel paragrafo: il clic non toglie la selezione dal foglio.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => ascoltaDa(ascolto)}
              className="pointer-events-auto inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-black/25 hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 dark:bg-white dark:text-slate-900 dark:hover:bg-gray-100"
            >
              <Play className="h-3.5 w-3.5" aria-hidden="true" />
              {audioAscolto.eliminatoAt
                ? `Vai alla trascrizione · ${formattaMs(ascolto.daMs)}`
                : `Ascolta da ${formattaMs(ascolto.daMs)}`}
            </button>
          </div>
        )}

        {r && (
          <VerbaleConsenso
            aperto={consensoAperto}
            onAnnulla={() => setConsensoAperto(false)}
            onConferma={() => {
              setConsensoAperto(false);
              setConsensoDato(true);
              mostraAudio();
              r.avvia({ avvisoConfermato: true });
            }}
          />
        )}
        {audioDisponibile && (
          <VerbaleVoci
            eventId={evento._id}
            aperto={vociAperte}
            onChiudi={() => setVociAperte(false)}
            onSalvato={() => {
              setVersioneVoci((v) => v + 1);
              leggiAudio();
            }}
          />
        )}
        <ConfirmDialog
          isOpen={!!uscita}
          onClose={() => setUscita(null)}
          onConfirm={() => { uscita?.(); }}
          title="Registrazione in corso"
          message={TESTO_USCITA_REGISTRAZIONE}
          confirmText="Esci comunque"
          cancelText="Resta qui"
          type="warning"
        />
      </>
    );
  };

  return shell(audioDisponibile ? (
    <ConRegistrazione eventId={evento._id} onAudioCambiato={leggiAudio} onAttiva={setRegistrazioneAttiva}>
      {vistaAutore}
    </ConRegistrazione>
  ) : vistaAutore(null));
};

export default AssociativeVerbale;
